"use client";

import { FileText, Mail, MapPin, Menu, Moon, Phone, Shield, Sun, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useTheme } from "next-themes";
import { useState } from "react";

interface OrgSiteHeaderProps {
  orgName: string;
  orgType: string;
  address: string;
  logoUrl?: string;
  slug: string;
  phone?: string;
  email?: string;
}

export function OrgSiteHeader({
  orgName,
  orgType,
  address,
  logoUrl,
  slug,
  phone,
  email,
}: OrgSiteHeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, setTheme } = useTheme();

  const navItems = [
    { label: "About", href: `/org/${slug}`, icon: null },
    { label: "Contact", href: `/org/${slug}/contact`, icon: Mail },
    { label: "Refund Policy", href: `/org/${slug}/refund-policy`, icon: FileText },
    { label: "Terms", href: `/org/${slug}/terms`, icon: FileText },
    { label: "Privacy", href: `/org/${slug}/privacy`, icon: Shield },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-card/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <div className="flex items-center gap-3">
          {logoUrl ? (
            <Image
              src={logoUrl}
              alt={orgName}
              width={40}
              height={40}
              className="h-10 w-10 rounded-lg object-cover"
              // Tenant logos live on tenant-controlled hosts; can't be allowlisted.
              unoptimized
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-lg">
              {orgName.charAt(0)}
            </div>
          )}
          <div>
            <Link
              href={`/org/${slug}`}
              className="text-lg font-bold text-foreground hover:text-primary transition-colors"
            >
              {orgName}
            </Link>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="rounded-full bg-accent px-2 py-0.5 text-accent-foreground font-medium">
                {orgType.replace(/_/g, " ")}
              </span>
              {phone && (
                <a
                  href={`tel:${phone}`}
                  className="hidden items-center gap-1 sm:flex hover:text-foreground transition-colors"
                >
                  <Phone className="h-3 w-3 shrink-0" />
                  {phone}
                </a>
              )}
              {email && (
                <a
                  href={`mailto:${email}`}
                  className="hidden items-center gap-1 md:flex hover:text-foreground transition-colors"
                >
                  <Mail className="h-3 w-3 shrink-0" />
                  <span className="line-clamp-1">{email}</span>
                </a>
              )}
              {address && (
                <span className="hidden items-center gap-1 lg:flex">
                  <MapPin className="h-3 w-3 shrink-0" />
                  <span className="line-clamp-1">{address}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <Link
              key={item.label}
              href={item.href}
              className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              {item.icon && <item.icon className="h-4 w-4" />}
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1">
          {/* Dark mode toggle */}
          <button
            type="button"
            className="relative rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            aria-label="Toggle dark mode"
          >
            <Sun className="h-5 w-5 rotate-0 scale-100 transition-transform dark:rotate-90 dark:scale-0" />
            <Moon className="absolute left-2 top-2 h-5 w-5 rotate-90 scale-0 transition-transform dark:rotate-0 dark:scale-100" />
          </button>

          {/* Mobile menu button */}
          <button
            type="button"
            className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent lg:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileMenuOpen && (
        <nav className="border-t border-border bg-card px-6 py-4 lg:hidden">
          <div className="flex flex-col gap-1">
            {navItems.map((item) => (
              <Link
                key={item.label}
                href={item.href}
                className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.icon && <item.icon className="h-4 w-4" />}
                {item.label}
              </Link>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}
